import HttpClient from '@src/shared/services/base/http-client';
import MCPMarketEnvironmentDetection, {
  MCP_MARKET_ENVIRONMENT_COUNTRY_CODES,
} from '@src/shared/utils/market-environment-detection';

export type TCourierSettings = {
  showUnassignedOrders: Boolean;
};

export type TCourier = {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  dateAdded: Date;
  status: COURIER_STATUS;
  hash: string;
  settings: TCourierSettings;
  isVerified?: boolean;
};

export enum COURIER_STATUS {
  NOT_REGISTERED = 'NOT_REGISTERED',
  INVITATION_SENT = 'INVITATION_SENT',
  CONNECTED = 'CONNECTED',
  DISCONNECTED = 'DISCONNECTED',
  DECLINED = 'DECLINED',
  ACCEPTED = 'ACCEPTED',
  PENDING_RESTAURANT_ACCEPTANCE = 'PENDING_RESTAURANT_ACCEPTANCE',
  DELETE = 'DELETE',
}

export type TCourierParam = {
  courierEmail: string;
  restaurantName: string;
};

export type TCourierDelete = {
  value: string[];
};

export enum LINK_COURIER_ERROR {
  INVALID_EMAIL = 'INVALID_EMAIL',
  COURIER_NOT_FOUND = 'COURIER_NOT_FOUND',
  RESTAURANT_NOT_FOUND = 'RESTAURANT_NOT_FOUND',
  CONNECTION_NOT_FOUND = 'CONNECTION_NOT_FOUND',
  CONNECTION_EXISTS = 'CONNECTION_EXISTS',
  INVALID_PARAMS = 'INVALID_PARAMS',
}

export type TGetCouriers = () => Promise<TCourier[]>;
export type TLinkCourier = (params: TCourierParam) => Promise<TCourier>;
export type TDisconnectCourier = (courierId: string) => Promise<TCourier>;
export type TConnectCourier = (courierId: string) => Promise<TCourier>;
export type TDeclineCourier = (courierId: string) => Promise<TCourier>;
export type TAcceptCourier = (courierId: string) => Promise<TCourier>;
export type TReinviteCourier = (courierEmail: string) => Promise<TCourier>;
export type TDeleteCourier = (courierHash: string) => Promise<TCourier>;

class CouriersApiService extends HttpClient {
  constructor(baseURL: string, authHeaderName: string) {
    super(baseURL, authHeaderName);
  }

  public getCouriers: TGetCouriers = () => {
    return this.instance.get('/couriers');
  };

  public linkCourier: TLinkCourier = (params) => {
    return this.instance.post('/couriers', params);
  };

  public disconnectCourier: TDisconnectCourier = (courierId) => {
    return this.instance.put('/couriers', {
      courierId,
      restaurantConnectionStatus: COURIER_STATUS.DISCONNECTED,
    });
  };

  public connectCourier: TConnectCourier = (courierId) => {
    return this.instance.put('/couriers', {
      courierId,
      restaurantConnectionStatus: COURIER_STATUS.ACCEPTED,
    });
  };

  public declineCourier: TDeclineCourier = (courierId) => {
    return this.instance.put('/couriers', {
      courierId,
      restaurantConnectionStatus: COURIER_STATUS.DECLINED,
    });
  };

  public reinviteCourier: TReinviteCourier = (courierEmail) => {
    return this.instance.post('/couriers/reinvite', { courierEmail });
  };

  public deleteCourier: TDeleteCourier = (courierHash: string) => {
    return this.instance.delete(`/couriers?hash=${courierHash}`);
  };
}

const getBaseUrl = () => {
  const { countryCode } = MCPMarketEnvironmentDetection.getMarketEnvironment();
  if (countryCode === MCP_MARKET_ENVIRONMENT_COUNTRY_CODES.UK) {
    return process.env.COURIERS_API_URL_EU_WEST as string;
  }
  return process.env.COURIERS_API_URL_EU_CENTRAL as string;
};

export default new CouriersApiService(getBaseUrl(), 'X-Auth');
